import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Button, Card, ConfirmDialog, DataGrid, KpiCard, Modal, Spinner, useToast,
  type DataGridColumn,
} from '@kss/ui';
import { apiClient } from '@kss/api';
import {
  AlertTriangle, ArrowLeft, CheckCircle2, Copy, Eye, EyeOff, KeyRound, Plus,
  RefreshCw, Trash2,
} from 'lucide-react';

/**
 * API key management.
 *
 * - List keys from `/api/identity/ApiKey` (or fall back to
 *   `/api/auth/admin/api-keys` if that's what the backend wired up).
 * - Create a key — POST → server returns the secret ONCE; we expose it
 *   in a one-time reveal box with a Copy button.
 * - Revoke — DELETE the key, with a confirm dialog because keys are
 *   often baked into integrations.
 *
 * KPIs an admin actually checks: total · active · expiring soon · revoked.
 */

interface ApiKeyRow {
  id?: number | string;
  apiKeyId?: string;
  name?: string;
  prefix?: string;
  scopes?: string[] | string;
  /** Backend stores scopes as a JSON-string column. */
  scopesJson?: string;
  expiresAt?: string;
  lastUsedAt?: string;
  isActive?: boolean | number;
  isRevoked?: boolean | number;
  revokedAt?: string;
  createdAt?: string;
}

interface CreatedKey {
  id?: number | string;
  apiKeyId?: string;
  /** Returned ONCE on create. The admin controller returns it as `apiKey`. */
  apiKey?: string;
  secret?: string;
  token?: string;
  plainTextKey?: string;
}

// The admin endpoint is the one that actually GENERATES a key and returns the
// plaintext secret ONCE. The generic registry CRUD (/identity/ApiKey) only
// stores a row — POSTing there never returns a usable secret. So the admin
// path is primary; CRUD is a read-only fallback for listing.
const PRIMARY_PATH = '/auth/admin/api-keys';
const FALLBACK_PATH = '/identity/ApiKey';

const inputCls =
  'h-9 w-full rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';

export default function ApiKeysPage() {
  const navigate = useNavigate();
  const toast = useToast();

  const [rows, setRows] = useState<ApiKeyRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [activePath, setActivePath] = useState(PRIMARY_PATH);

  const [createOpen, setCreateOpen] = useState(false);
  const [createdKey, setCreatedKey] = useState<{ name: string; secret: string } | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<ApiKeyRow | null>(null);
  const [revoking, setRevoking] = useState(false);

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      // Try the primary path first; fall back to the admin path if the
      // backend exposes it there instead.
      try {
        const r = await apiClient.get(PRIMARY_PATH);
        setRows(pickList<ApiKeyRow>(r.data));
        setActivePath(PRIMARY_PATH);
      } catch {
        const r = await apiClient.get(FALLBACK_PATH);
        setRows(pickList<ApiKeyRow>(r.data));
        setActivePath(FALLBACK_PATH);
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { void load(); }, []);

  const kpis = useMemo(() => {
    const total = rows.length;
    const active = rows.filter((r) => isActive(r)).length;
    const revoked = rows.filter((r) => r.isRevoked === true || r.isRevoked === 1 || r.isActive === false || r.isActive === 0 || !!r.revokedAt).length;
    const soon = Date.now() + 30 * 24 * 60 * 60 * 1000;
    const expiringSoon = rows.filter((r) => {
      if (!r.expiresAt) return false;
      const t = Date.parse(String(r.expiresAt));
      return !Number.isNaN(t) && t > Date.now() && t < soon;
    }).length;
    return { total, active, revoked, expiringSoon };
  }, [rows]);

  async function revoke() {
    if (!confirmRevoke) return;
    const id = String(confirmRevoke.apiKeyId ?? confirmRevoke.id ?? '');
    const prefix = String(confirmRevoke.prefix ?? '');
    if (!id && !prefix) return;
    setRevoking(true);
    try {
      // The admin endpoint revokes by prefix via POST /revoke; fall back to a
      // CRUD DELETE-by-id for the registry path.
      try {
        await apiClient.post(`${activePath}/revoke`, { prefix, reason: 'Revoked from admin console', id, apiKeyId: id });
      } catch {
        await apiClient.delete(`${activePath}/${encodeURIComponent(id)}`);
      }
      toast.success('Revoked', `${confirmRevoke.name ?? id.slice(0, 8)} can no longer authenticate`);
      setConfirmRevoke(null);
      await load();
    } catch (e) {
      toast.error('Revoke failed', e instanceof Error ? e.message : 'Network error');
    } finally {
      setRevoking(false);
    }
  }

  const columns = useMemo<DataGridColumn<ApiKeyRow>[]>(
    () => [
      { accessor: 'name', title: 'Name', sortable: true, filterable: true,
        render: (r) => (
          <div>
            <div className="font-medium text-content-primary">{String(r.name ?? '—')}</div>
            {r.prefix && (
              <code className="text-[11px] text-content-muted">{String(r.prefix)}…</code>
            )}
          </div>
        )
      },
      { accessor: 'scopes', title: 'Scopes',
        render: (r) => (
          <span className="line-clamp-1 max-w-[280px] text-xs text-content-secondary">
            {scopesOf(r).join(' · ') || '—'}
          </span>
        )
      },
      { accessor: 'lastUsedAt', title: 'Last used', sortable: true,
        render: (r) => fmtDate(r.lastUsedAt) },
      { accessor: 'expiresAt', title: 'Expires', sortable: true,
        render: (r) => {
          if (!r.expiresAt) return <span className="text-content-muted">—</span>;
          const t = Date.parse(String(r.expiresAt));
          const overdue = !Number.isNaN(t) && t < Date.now();
          return (
            <span className={overdue ? 'text-danger' : ''}>
              {fmtDate(r.expiresAt)}
              {overdue && ' (expired)'}
            </span>
          );
        }
      },
      { accessor: 'isActive', title: 'Status',
        render: (r) => isActive(r)
          ? <span className="inline-flex rounded-full border border-success/40 bg-success/10 px-2 py-0.5 text-[11px] text-success">active</span>
          : <span className="inline-flex rounded-full border border-danger/40 bg-danger/10 px-2 py-0.5 text-[11px] text-danger">revoked</span>
      },
      { accessor: '__actions' as never, title: '', width: 60,
        render: (r) => isActive(r) ? (
          <button
            type="button"
            onClick={() => setConfirmRevoke(r)}
            title="Revoke key"
            className="text-content-muted hover:text-danger"
          >
            <Trash2 size={14} />
          </button>
        ) : null
      },
    ],
    [],
  );

  return (
    <div className="-m-6 space-y-3 p-[3px]">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-surface-card px-4 py-3">
        <Button variant="outline" size="sm" onClick={() => navigate('/panel/profile')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-semibold text-content-primary">API keys</h1>
          <p className="text-xs text-content-muted">
            Programmatic access to the BillBook API. Keys carry the same scope as the user that
            created them.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Reload
        </Button>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="h-3.5 w-3.5" /> New key
        </Button>
      </div>

      {err && <Alert variant="danger">{err}</Alert>}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard title="Total keys"     value={kpis.total.toLocaleString()}     subtitle="All issued"
          icon={<KeyRound className="h-5 w-5 text-primary" />} />
        <KpiCard title="Active"          value={kpis.active.toLocaleString()}    subtitle="Can authenticate"
          icon={<CheckCircle2 className="h-5 w-5 text-success" />} />
        <KpiCard title="Expiring (30d)"  value={kpis.expiringSoon.toLocaleString()} subtitle={kpis.expiringSoon ? 'Rotate soon' : 'Nothing imminent'}
          icon={<AlertTriangle className={`h-5 w-5 ${kpis.expiringSoon ? 'text-warning' : 'text-success'}`} />} />
        <KpiCard title="Revoked"         value={kpis.revoked.toLocaleString()}   subtitle="No longer valid"
          icon={<Trash2 className="h-5 w-5 text-danger" />} />
      </div>

      {loading && rows.length === 0 ? (
        <Card>
          <div className="flex items-center gap-2 p-6 text-sm text-content-muted">
            <Spinner /> Loading keys…
          </div>
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <div className="p-6 text-sm text-content-muted">
            No API keys yet. Click <em>New key</em> to create one — you&rsquo;ll see the secret
            exactly once and won&rsquo;t be able to retrieve it later.
          </div>
        </Card>
      ) : (
        <DataGrid<ApiKeyRow>
          data={rows}
          columns={columns}
          rowId={(rows[0]?.apiKeyId != null ? 'apiKeyId' : 'id') as keyof ApiKeyRow & string}
        />
      )}

      <CreateKeyModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        path={activePath}
        onCreated={(name, secret) => {
          setCreateOpen(false);
          setCreatedKey({ name, secret });
          void load();
        }}
      />

      <RevealKeyModal
        open={!!createdKey}
        onClose={() => setCreatedKey(null)}
        name={createdKey?.name ?? ''}
        secret={createdKey?.secret ?? ''}
      />

      <ConfirmDialog
        isOpen={!!confirmRevoke}
        title="Revoke this API key?"
        message={
          confirmRevoke
            ? `${confirmRevoke.name ?? '—'}${
                confirmRevoke.prefix ? ` · ${confirmRevoke.prefix}…` : ''
              } — any client using this key will start failing immediately.`
            : ''
        }
        confirmLabel={revoking ? 'Revoking…' : 'Revoke'}
        onClose={() => setConfirmRevoke(null)}
        onConfirm={revoke}
        variant="danger"
      />
    </div>
  );
}

/* -------------------------------------------------------------------- */
/*  Create modal                                                        */
/* -------------------------------------------------------------------- */

function CreateKeyModal({
  open, onClose, path, onCreated,
}: {
  open: boolean;
  onClose: () => void;
  path: string;
  onCreated: (name: string, secret: string) => void;
}) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState('read write');
  const [expiresInDays, setExpiresInDays] = useState<number | ''>(90);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName('');
      setScopes('read write');
      setExpiresInDays(90);
      setErr(null);
    }
  }, [open]);

  async function submit() {
    if (!name.trim()) {
      setErr('Name is required.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const expiresAt = expiresInDays
        ? new Date(Date.now() + Number(expiresInDays) * 86_400_000).toISOString()
        : undefined;
      const scopeList = scopes.split(/[\s,]+/).filter(Boolean);
      // Shape expected by the admin key-generation endpoint. It returns the
      // plaintext key ONCE as `apiKey`.
      const payload = {
        name,
        scopes: scopeList,
        expiresAt,
        rateLimitPerMinute: 0,
        ipAllowlist: [],
      };
      const r = await apiClient.post(path, payload);
      const d = (r.data?.data ?? r.data) as CreatedKey;
      const secret = String(d?.apiKey ?? d?.plainTextKey ?? d?.secret ?? d?.token ?? '');
      if (!secret) {
        // Endpoint echoed the row without the plaintext secret.
        toast.error('Created, but no secret returned', 'The key was saved — generate via the admin API to reveal a secret.');
      }
      onCreated(name, secret);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Create failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="New API key" width={520}>
      {err && <Alert variant="danger" className="mb-3">{err}</Alert>}
      <div className="space-y-3">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-content-secondary">
            Name <span className="text-danger">*</span>
          </span>
          <input
            className={inputCls}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="POS terminal · Mumbai"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-content-secondary">Scopes (space-separated)</span>
          <input
            className={inputCls}
            value={scopes}
            onChange={(e) => setScopes(e.target.value)}
            placeholder="read write pos.charge"
          />
          <span className="text-[11px] text-content-muted">
            Leave broad (<code>read write</code>) for general use; narrow it for shared
            integrations (e.g. <code>pos.charge restaurant.kot</code>).
          </span>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-content-secondary">Expires in (days)</span>
          <input
            type="number"
            className={inputCls}
            value={expiresInDays}
            min={1}
            max={3650}
            onChange={(e) => setExpiresInDays(e.target.value === '' ? '' : Number(e.target.value))}
          />
          <span className="text-[11px] text-content-muted">
            Leave empty for a non-expiring key (not recommended for production integrations).
          </span>
        </label>
      </div>
      <div className="mt-4 flex justify-end gap-2 border-t border-border pt-3">
        <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={submit} disabled={busy}>
          <KeyRound className="h-4 w-4" /> {busy ? 'Creating…' : 'Create key'}
        </Button>
      </div>
    </Modal>
  );
}

/* -------------------------------------------------------------------- */
/*  Reveal modal — shown ONCE                                           */
/* -------------------------------------------------------------------- */

function RevealKeyModal({
  open, onClose, name, secret,
}: {
  open: boolean;
  onClose: () => void;
  name: string;
  secret: string;
}) {
  const toast = useToast();
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (open) setRevealed(false);
  }, [open]);

  function copy() {
    if (!secret) return;
    navigator.clipboard
      .writeText(secret)
      .then(() => toast.success('Copied', 'Paste it into your integration now — it won\'t be shown again.'))
      .catch(() => toast.error('Copy failed', 'Select the value and copy manually.'));
  }

  return (
    <Modal open={open} onClose={onClose} title={`API key: ${name}`} width={560}>
      <Alert variant="warning">
        Save this secret <strong>now</strong>. We don&rsquo;t store it in plaintext — once you close
        this dialog you cannot retrieve it again.
      </Alert>
      <div className="mt-4 flex flex-col gap-2">
        <span className="text-xs font-medium text-content-secondary">API key</span>
        <div className="flex gap-2">
          <code className="flex-1 break-all rounded-md border border-border bg-surface-card px-3 py-2 font-mono text-xs">
            {secret ? (revealed ? secret : '•'.repeat(Math.min(secret.length, 48))) : '(no secret returned by the backend)'}
          </code>
          <Button variant="outline" onClick={() => setRevealed((r) => !r)} aria-label={revealed ? 'Hide' : 'Reveal'}>
            {revealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </Button>
          <Button onClick={copy} disabled={!secret}>
            <Copy className="h-4 w-4" /> Copy
          </Button>
        </div>
        <div className="mt-2 rounded-md border border-border bg-surface-card-hover p-3 text-[11px] text-content-secondary">
          <div className="font-semibold text-content-primary">Use it like:</div>
          <code className="mt-1 block whitespace-pre font-mono text-xs">
            curl https://api.ebillbook.kristysoftware.com/api/master-data/Bank \{'\n'}
            {'  '}-H "Authorization: Bearer {secret ? '<paste-here>' : '<your-key>'}"
          </code>
        </div>
      </div>
      <div className="mt-4 flex justify-end gap-2 border-t border-border pt-3">
        <Button onClick={onClose}>I&rsquo;ve saved it</Button>
      </div>
    </Modal>
  );
}

/* -------------------------------------------------------------------- */
/*  Helpers                                                             */
/* -------------------------------------------------------------------- */

/** Read scopes from the backend `scopesJson` string column, falling back to a
 *  `scopes` array/string for older shapes. */
function scopesOf(r: ApiKeyRow): string[] {
  if (typeof r.scopesJson === 'string' && r.scopesJson.trim()) {
    try {
      const parsed = JSON.parse(r.scopesJson);
      if (Array.isArray(parsed)) return parsed.map(String);
    } catch { return r.scopesJson.split(/[\s,]+/).filter(Boolean); }
  }
  if (Array.isArray(r.scopes)) return r.scopes.map(String);
  if (typeof r.scopes === 'string' && r.scopes.trim()) return r.scopes.split(/[\s,]+/).filter(Boolean);
  return [];
}

function isActive(r: ApiKeyRow): boolean {
  if (r.isRevoked === true || r.isRevoked === 1) return false;
  if (r.isActive === false || r.isActive === 0) return false;
  if (r.expiresAt) {
    const t = Date.parse(String(r.expiresAt));
    if (!Number.isNaN(t) && t < Date.now()) return false;
  }
  return true;
}

function fmtDate(v: unknown): string {
  if (typeof v !== 'string' || !v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v.slice(0, 10);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function pickList<T>(d: unknown): T[] {
  if (Array.isArray(d)) return d as T[];
  if (d && typeof d === 'object') {
    const obj = d as { items?: unknown[]; data?: unknown[] | { items?: unknown[] } };
    if (Array.isArray(obj.items)) return obj.items as T[];
    if (Array.isArray(obj.data)) return obj.data as T[];
    if (obj.data && Array.isArray((obj.data as { items?: unknown[] }).items)) {
      return (obj.data as { items: unknown[] }).items as T[];
    }
  }
  return [];
}
